import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationBarComponent } from './common/components/navigation-bar/navigation-bar.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LanguageSwitcherComponent } from './common/components/navigation-bar/language-switcher/language-switcher.component';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { HomeComponent } from './common/pages/home/home.component';
import { MatCardModule } from '@angular/material/card';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTabsModule } from '@angular/material/tabs';
import { RaceInfoComponent } from './common/pages/race-info/race-info.component';
import { RegisteredCrewsTableComponent } from './common/components/navigation-bar/competitions/competition-details/registered-crews-table/components/registered-crews-table.component';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatTableModule} from '@angular/material/table';
import {RegistrationDialogComponent} from './common/components/navigation-bar/registration-dialog/registration-dialog.component';
import {MatDialogModule} from '@angular/material/dialog';
import {MatDatepickerModule} from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatListModule} from '@angular/material/list';
import {AppRoutingModule} from './app-routing.module';
import {RouterModule} from '@angular/router';
import { AddCompetitionCardComponent } from './common/components/competition-management/add-competition-card/add-competition-card.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoginDialogComponent } from './common/components/navigation-bar/login-dialog/login-dialog.component';
import { ProfileInfoComponent } from './common/components/navigation-bar/profile-info/profile-info.component';
import { AddCompetitorCardComponent } from './common/components/navigation-bar/profile-info/add-competitor-card/add-competitor-card.component';
import { EditCompetitorCardComponent } from './common/components/navigation-bar/profile-info/edit-competitor-card/edit-competitor-card.component';
import { AddCrewCardComponent } from './common/components/navigation-bar/profile-info/add-crew-card/add-crew-card.component';
import { CompetitionDetailsComponent } from './common/components/navigation-bar/competitions/competition-details/competition-details.component';
import { SignInCardComponent } from './common/components/navigation-bar/competitions/competition-details/sign-in-card/sign-in-card.component';
import { EditCrewCardComponent } from './common/components/navigation-bar/profile-info/edit-crew-card/edit-crew-card.component';
import {DragDropModule} from '@angular/cdk/drag-drop';
import { MultipleTableCreatorComponent } from './common/components/competition-management/add-competition-card/add-stage-card/multiple-table-creator/multiple-table-creator.component';
import { MatTreeModule } from '@angular/material/tree';
import { ActivateComponent } from './common/components/activate/activate.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { RoundResultsTableComponent } from './common/components/round-result/components/round-results-table.component';
import { ConfirmationDialogComponent } from './common/components/confirmation-dialog/confirmation-dialog.component';
import { EditSpeedTrialResultComponent } from './common/components/competition-management/edit-competition-card/stage-results-table/speed-trial-result/edit-speed-trial-result/edit-speed-trial-result.component';
import { SeasonResultsTableComponent } from './common/components/season-management/season-results-table/season-results-table.component';
import { ForgotPasswordDialogComponent } from './common/components/navigation-bar/login-dialog/forgot-password-dialog/forgot-password-dialog.component';
import { ArchiveComponent } from './common/components/navigation-bar/archive/archive.component';
import { SpinnerComponent } from './controls/spinner/spinner.component';
import { CecDateTimePickerComponent } from './controls/date-input/date-input.component';
import { TableListComponent } from './controls/table-list/table-list.component';
import { CecBreakpointPipe } from './controls/pipes/cec-breakpoint.pipe';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DATE_FORMAT } from './controls/shared/date-format.const';
import { SeasonSpeedTrialResultsTableComponent } from './common/components/season-management/season-stage-results-table/season-speed-trial-results-table.component';
import { CompetitionsComponent } from './common/components/navigation-bar/competitions/competitions.component';
import { StageResultsTableComponent } from './common/components/stage-result/components/list/stage-results-table.component';
import { CompetitionResultsTableComponent } from './common/components/competition-result/components/competition-results-table.component';
import { ResultsComponent } from './common/components/navigation-bar/results/results.component';
import { SpinnerInterceptor } from './api/interceptors/spinner.interceptor';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient): any {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    NavigationBarComponent,
    LanguageSwitcherComponent,
    RaceInfoComponent,
    RegistrationDialogComponent,
    AddCompetitionCardComponent,
    LoginDialogComponent,
    ProfileInfoComponent,
    AddCompetitorCardComponent,
    EditCompetitorCardComponent,
    AddCrewCardComponent,
    CompetitionDetailsComponent,
    SignInCardComponent,
    EditCrewCardComponent,
    MultipleTableCreatorComponent,
    ActivateComponent,
    ConfirmationDialogComponent,
    EditSpeedTrialResultComponent,
    ForgotPasswordDialogComponent,
    ArchiveComponent,
    SpinnerComponent,
    SeasonSpeedTrialResultsTableComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule.forRoot({
      loader: {
      provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MatSelectModule,
    MatMenuModule,
    MatDividerModule,
    MatCardModule,
    MatGridListModule,
    MatTabsModule,
    MatPaginatorModule,
    MatTableModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatListModule,
    AppRoutingModule,
    RouterModule,
    MatTooltipModule,
    DragDropModule,
    MatTreeModule,
    MatExpansionModule,
    CecDateTimePickerComponent,
    TableListComponent,
    CecBreakpointPipe,
    MatSidenavModule,
    RegisteredCrewsTableComponent,
    CompetitionsComponent,
    StageResultsTableComponent,
    RoundResultsTableComponent,
    CompetitionResultsTableComponent,
    HomeComponent,
    ResultsComponent
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
